import React, { useEffect, useState } from "react";
import { Typography, Button } from "@mui/material";
import dayjs from "configs/dayjs.config";

import { useGlobalContext } from "context/GlobalContext";
import { useProductContext } from "../context";
import { PageTitle } from "components/PageMeta/PageTitle";
import { pageName } from "constants/pageNames";
import { PAGE_NOTES } from "constants/PageNotes";
import { ProductTopBanner } from "../components/ProductTopBanner";
import {
  EARLY_DELIVERY_OPTIONS,
  EXECUTION_TIMELINE_FIELDS,
} from "../constants/product.constants";
import { TextFieldWrapper } from "components/Wrappers/FormComponentsWrappers/TextFieldWrapper";
import { updateExecutionTimeline } from "API/apis/product/updateExecutionTimeline";
import { STATUS_CODES } from "constants/responses/statusCodes";
import { useNavigate } from "react-router-dom";
import { routeLinks } from "constants/paths";
import { dateTimeFormat } from "constants/componentsConstants";
import { DatePickerWrapper } from "components/DateTimePicker/DatePicker";
import { ExecutionTimelineTemplateSelector } from "../components/TemplateSelector";
import { FlexBox } from "components/Wrappers/FlexBox/FlexBox";
import { useParams } from "react-router-dom";
import { getProductExecutionTimelineApi } from "API/apis/product/getProductExecutionTimeline";
import { message } from "constants/responses/message";
import { SingleSelect } from "components/Select/SingleSelect";
import { getExecutionTimelineAssigneeApi } from "API/apis/product/getExecutionTimelineAssignee";
import { getExecutionTimelineAssigneeByLotIdApi } from "API/apis/product/getExecutionTimelineAssigneeByLotId";

const initialValue = {
  startDate: null,
  endDate: null,
  earlyDelivery: "Preferred",
  handover: 0,
  shopDrawing: 0,
  clientApprovalSd: 0,
  shopDrawingRevision: 0,
  clientApprovalSdr: 0,
  proto: 0,
  clientApprovalP: 0,
  protoRevision: 0,
  clientApprovalPr: 0,
  pcd: 0,
  protoProduction: 0,
  production: 0,
  specificPurchase: 0,
  buffer: 0,
};

export const ProductExecutionTimeline = () => {
  const { id } = useParams();

  const { setIsInitializing, setError, copy } = useGlobalContext();
  const [isReloadRequired, setIsReloadRequired] = useState(false);
  const { currentProject, setReloadRequired, reloadRequired } =
    useProductContext();
  const [formData, setFormData] = useState(initialValue);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    calculateFinalDate();
    getDefaultAssignee();
  }, [isReloadRequired]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsInitializing(true);
        if (copy) {
          const { data, status } = await getProductExecutionTimelineApi(
            Number(copy)
          );
          if (status !== 200) {
            throw new Error(message.error.generalFailure);
          }
          setStartDate(data[0].startDate);
          setFormData(data[0]);
        } else {
          setStartDate(null);
          setFormData({});
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setIsInitializing(false);
        setIsReloadRequired(!isReloadRequired);
      }
    };
    fetchData();
  }, []);
  const getDefaultAssignee = async () => {
    try {
      const promises = [
        getExecutionTimelineAssigneeByLotIdApi(copy),
        getExecutionTimelineAssigneeApi(),
      ];
      const [copiedAssignee, defaultAssignee] = await Promise.all(promises);
      if (defaultAssignee.status !== 200) {
        throw new Error(message.error.generalFailure);
      }
      setAssigneeOptions(
        defaultAssignee.data.map((item) => ({
          option: item.userId,
          value: item.name,
        }))
      );

      if (copiedAssignee.status !== 200) {
        let tempFormDataAssignee = {};
        defaultAssignee.data.forEach((item) => {
          tempFormDataAssignee = {
            ...tempFormDataAssignee,
            ["assignee_" + item.field]: item.userId,
          };
        });
        setFormData({
          ...formData,
          ...tempFormDataAssignee,
        });
      } else {
        let tempFormDataAssignee = {};
        copiedAssignee.data.forEach((item) => {
          tempFormDataAssignee = {
            ...tempFormDataAssignee,
            ["assignee_" + item.field]: item.userId,
          };
        });
        setFormData({
          ...formData,
          ...tempFormDataAssignee,
        });
      }
    } catch (error) {
      setError(error.message);
    }
  };
  const calculateFinalDate = () => {
    const totalDays = Object.entries(EXECUTION_TIMELINE_FIELDS).reduce(
      (acc, [key]) => {
        return Number(acc) + Number(formData[key]);
      },
      0
    );
    setEndDate(dayjs(startDate).add(totalDays, "day").format("YYYY-MM-DD"));
  };
  const handleStartDateChange = (newValue) => {
    setStartDate(dayjs(newValue).format(dateTimeFormat.date));
    setFormData({
      ...formData,
      startDate: dayjs(newValue).format(dateTimeFormat.date),
    });
    setIsReloadRequired(!isReloadRequired);
  };
  const handleEndDateChange = (newValue) => {
    setEndDate(dayjs(newValue).format(dateTimeFormat.date));
    setFormData({
      ...formData,
      endDate: dayjs(newValue).format(dateTimeFormat.date),
    });
    setIsReloadRequired(!isReloadRequired);
  };
  const handleTextFieldChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
    setIsReloadRequired(!isReloadRequired);
  };
  const handleSingleSelect = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };
  const handleSubmit = async () => {
    const { data, status } = await updateExecutionTimeline({
      ...formData,
      startDate: dayjs(startDate).add(1).format(dateTimeFormat.date),
      endDate,
      productLotProcessingId: id.split(","),
    });
    if (status !== STATUS_CODES.SUCCESS) {
      throw new Error(data.message);
    }
    setReloadRequired(!reloadRequired);
    navigate(routeLinks.projectManagementProject + "/" + currentProject);
  };
  return (
    <>
      <PageTitle pageName={pageName.product.executionTimeline} />
      <Typography fontStyle="italic">
        Note: {PAGE_NOTES.executionTimeline}
      </Typography>
      <FlexBox>
        <ExecutionTimelineTemplateSelector
          setFormData={setFormData}
          isReloadRequired={isReloadRequired}
          setIsReloadRequired={setIsReloadRequired}
        />
      </FlexBox>
      <ProductTopBanner buttonType="back" />
      <form onSubmit={handleSubmit}>
        <DatePickerWrapper
          label="Start Date"
          name="startDate"
          value={dayjs(startDate)}
          onChange={(newValue) => handleStartDateChange(dayjs(newValue))}
          disabled={
            formData?.earlyDelivery === EARLY_DELIVERY_OPTIONS.no ? true : false
          }
          //TODO
          // minDate={dayjs()}
        />
        <DatePickerWrapper
          label="End Date"
          name="endDate"
          value={dayjs(endDate)}
          onChange={(newValue) => handleEndDateChange(dayjs(newValue))}
          disabled={
            formData?.earlyDelivery === EARLY_DELIVERY_OPTIONS.no ? false : true
          }
        />
        {/* ===============================timelines============================== */}
        {Object.entries(EXECUTION_TIMELINE_FIELDS).map(
          ([key, value], index) => {
            return (
              <FlexBox key={index}>
                <TextFieldWrapper
                  id={key}
                  name={key}
                  label={value}
                  variant="outlined"
                  type="number"
                  value={formData?.[key] || 0}
                  fullWidth
                  onChange={handleTextFieldChange}
                  inputProps={{ min: 0 }}
                />
                <SingleSelect
                  label="Select Assignee"
                  name={"assignee_" + key}
                  handleSelectSingle={(e) => handleSingleSelect(e)}
                  options={assigneeOptions}
                  selectedOption={formData?.["assignee_" + key]}
                />
              </FlexBox>
            );
          }
        )}

        {/* ===============================timelines============================== */}
        <Button onClick={handleSubmit} variant="contained" fullWidth>
          Update
        </Button>
      </form>
    </>
  );
};
